import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'

interface ParsedDeepLink {
  paymentId: string | null
  code: string | null
  message: string | null
}

interface SafeUrlInfo {
  protocol: string | null
  hostname: string | null
  pathname: string | null
  queryKeys: string[]
}

function describeUrlSafely(rawUrl: string): SafeUrlInfo {
  try {
    const parsed = new URL(rawUrl)
    return {
      protocol: parsed.protocol,
      hostname: parsed.hostname,
      pathname: parsed.pathname,
      queryKeys: [...parsed.searchParams.keys()],
    }
  }
  catch {
    return { protocol: null, hostname: null, pathname: null, queryKeys: [] }
  }
}

function parsePaymentCompleteUrl(rawUrl: string): ParsedDeepLink | null {
  let parsed: URL

  try {
    parsed = new URL(rawUrl)
  }
  catch {
    return null
  }

  if (parsed.protocol !== 'ooingpay:' || parsed.hostname !== 'payment-complete')
    return null

  return {
    paymentId: parsed.searchParams.get('paymentId') ?? parsed.searchParams.get('payment_id'),
    code: parsed.searchParams.get('code'),
    message: parsed.searchParams.get('message'),
  }
}

let lastHandledUrl: string | null = null

function handleIncomingUrl(source: 'open' | 'launch', rawUrl: string) {
  const timestamp = new Date().toISOString()

  console.warn('[DeepLink][received]', { url: rawUrl, timestamp })

  if (rawUrl === lastHandledUrl) {
    console.warn('[DeepLink:duplicate]', { ...describeUrlSafely(rawUrl), timestamp })
    return
  }
  lastHandledUrl = rawUrl

  const parsed = parsePaymentCompleteUrl(rawUrl)
  const tag = source === 'open' ? '[DeepLink:open]' : '[DeepLink:launch]'

  if (!parsed) {
    console.warn('[DeepLink:ignored]', { ...describeUrlSafely(rawUrl), timestamp })
    return
  }

  console.warn('[DeepLink][parsed]', {
    paymentId: parsed.paymentId,
    code: parsed.code,
    message: parsed.message,
  })

  console.warn(tag, {
    ...describeUrlSafely(rawUrl),
    hasPaymentId: parsed.paymentId != null,
    hasCode: parsed.code != null,
    hasMessage: parsed.message != null,
    timestamp,
  })
}

export default defineNuxtPlugin(() => {
  if (!Capacitor.isNativePlatform())
    return

  App.addListener('appUrlOpen', (data) => {
    try {
      handleIncomingUrl('open', data.url)
    }
    catch (error) {
      console.error('[DeepLink:error]', {
        name: error instanceof Error ? error.name : undefined,
        message: error instanceof Error ? error.message : String(error),
      })
    }
  })

  App.getLaunchUrl()
    .then((result) => {
      if (result?.url)
        handleIncomingUrl('launch', result.url)
    })
    .catch((error) => {
      console.error('[DeepLink:error]', {
        name: error instanceof Error ? error.name : undefined,
        message: error instanceof Error ? error.message : String(error),
      })
    })
})
